import { sectionRenderer } from '@theme/section-renderer';

const CHOICE_KEY = 'essntl_sp_choice';

class EssntlShippingProtection extends HTMLElement {
  #controller = new AbortController();
  #busy = false;

  connectedCallback() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.switch?.addEventListener('change', this.#handleChange, { signal });
    this.closest('cart-items-component')?.addEventListener('click', this.#handleCartItemClick, {
      capture: true,
      signal,
    });
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get switch() {
    return this.querySelector('[data-sp-switch]');
  }

  get status() {
    return this.querySelector('[data-sp-status]');
  }

  get variantId() {
    return Number(this.dataset.variantId || 0);
  }

  #getChoice() {
    try {
      return sessionStorage.getItem(CHOICE_KEY) === 'on';
    } catch (_) {
      return false;
    }
  }

  #setChoice(enabled) {
    try {
      sessionStorage.setItem(CHOICE_KEY, enabled ? 'on' : 'off');
    } catch (_) {
      // The cart still works when storage is unavailable; opt-in defaults off.
    }
  }

  #isProtectionItem(item) {
    if (!item) return false;
    if (Number(item.variant_id) === this.variantId) return true;
    return Boolean(item.properties?._essntl_shipping_protection);
  }

  #protectionLine(cart) {
    return cart?.items?.find((item) => this.#isProtectionItem(item)) || null;
  }

  #hasMerchandise(cart) {
    return Boolean(cart?.items?.some((item) => !this.#isProtectionItem(item)));
  }

  #route(path) {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? `${root}${path}` : `${root}/${path}`;
  }

  async #getCart() {
    const response = await fetch(this.#route('cart.js'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Could not load cart (${response.status})`);
    return response.json();
  }

  async #changeLine(key, quantity) {
    const response = await fetch(this.#route('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!response.ok) throw new Error(`Could not update protection (${response.status})`);
    return response.json();
  }

  async #addProtection() {
    const response = await fetch(this.#route('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: this.variantId,
            quantity: 1,
            properties: { _essntl_shipping_protection: 'true' },
          },
        ],
      }),
    });
    if (!response.ok) throw new Error(`Could not add protection (${response.status})`);
    return this.#getCart();
  }

  async #reconcile(cart) {
    const line = this.#protectionLine(cart);
    const hasMerchandise = this.#hasMerchandise(cart);
    if (!hasMerchandise) this.#setChoice(false);
    const shouldHaveProtection = this.#getChoice() && hasMerchandise;

    if (shouldHaveProtection && !line) {
      return { cart: await this.#addProtection(), changed: true };
    }

    if (!shouldHaveProtection && line) {
      return { cart: await this.#changeLine(line.key, 0), changed: true };
    }

    if (shouldHaveProtection && line?.quantity !== 1) {
      return { cart: await this.#changeLine(line.key, 1), changed: true };
    }

    return { cart, changed: false };
  }

  #renderState(cart) {
    const hasMerchandise = this.#hasMerchandise(cart);
    const enabled = this.#getChoice() && hasMerchandise;
    this.hidden = !hasMerchandise;
    if (this.switch) {
      this.switch.checked = enabled;
      this.switch.disabled = this.#busy;
    }
    if (this.status) {
      this.status.textContent = enabled ? this.dataset.textOn || '' : this.dataset.textOff || '';
    }
  }

  async #refreshCartSection() {
    const cartItems = this.closest('cart-items-component');
    const sectionId = cartItems?.dataset.sectionId;
    if (!sectionId) return;
    await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'full' });
  }

  async #sync() {
    if (this.#busy || !this.variantId) return;
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const result = await this.#reconcile(await this.#getCart());
      this.#renderState(result.cart);
      if (result.changed) await this.#refreshCartSection();
    } catch (error) {
      console.error('[ESSNTL shipping protection]', error);
      if (this.switch) this.switch.checked = this.#getChoice();
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      if (this.switch) this.switch.disabled = false;
    }
  }

  #handleChange = () => {
    if (!this.switch || this.#busy) return;
    this.#setChoice(this.switch.checked);
    this.#sync();
  };

  #handleCartItemClick = (event) => {
    const lineKey = this.dataset.lineKey;
    if (!lineKey || !(event.target instanceof Element)) return;
    const row = event.target.closest('[data-key]');
    if (!row || row.getAttribute('data-key') !== lineKey) return;
    if (event.target.closest('.cart-items__remove, button[name="minus"]')) this.#setChoice(false);
  };
}

if (!customElements.get('essntl-shipping-protection')) {
  customElements.define('essntl-shipping-protection', EssntlShippingProtection);
}